import React from 'react';
import styled from 'styled-components';
import SvgIcon from './SvgIcon';
import { IconName, ISvgIconProps } from './types';
import { theme } from '../../styles/theme';

/**
 * ColorSvgIcon requires SVGs to be "instrumented" with .fill-color and .stroke-color classes to mark graphic elements
 * that could change their colors. For example, line/legend icons have occasional white outline which should remain
 * white if the main color changes
 */
const ColorSvgIcon = styled(SvgIcon)<{ $color: string }>`
  .fill-color {
    fill: ${({ $color }) => $color};
  }
  .stroke-color {
    stroke: ${({ $color }) => $color};
  }
`;

const inactiveColor = '#C4C9DE';

// ISvgIconProps['name'] will be determined by lookup in iconByIndex[index]
interface IIndexedIconProps extends Omit<ISvgIconProps, 'name'> {
  iconByIndex: IconName[];
  index: number;
  active: boolean;
  color: string;
}

/**
 * Render an icon based on the index in the iconByIndex list. The color of the icon follows the indicies
 * of slice colors.
 *
 * This component is not meant to be used directly, but serves as basis of {@link ChartLineIcon} and
 * {@link ChartSliceIcon}
 *
 * @param index - the index of the icon (0-based)
 * @param active - if true, the color is render with a color designated for the slice at that index. If false, render it
 * with muted color
 * @param iconByIndex - the list {@link IconName} to select icon from
 * @param color - option to provide custom color
 * @param svgIconProps - any other props that {@link SvgIcon} accepts ({@see ISvgIconProps})
 * @constructor
 */
const IndexedIcon = ({
  index,
  active,
  iconByIndex,
  // just in case, mainBlue color would be used as a fallback
  color = theme.colors.mainBlue,
  ...svgIconProps
}: IIndexedIconProps) => {
  const iconName = iconByIndex[index % iconByIndex.length];

  return (
    <ColorSvgIcon
      {...svgIconProps}
      name={iconName}
      $color={active ? color : inactiveColor}
    />
  );
};

const pathSliceIcons: IconName[] = ['legend-path-0', 'legend-path-1'];

/**
 * Renders icon using LegendPath0-1 icon set. It displays the icons of slices in Path
 * Icons always use the mainBlue color, they use pattern to distinct slices
 */
type IPathSliceIconProps = Omit<IIndexedIconProps, 'iconByIndex' | 'color'>;
export const PathSliceIcon = ({
  active,
  ...legendProps
}: IPathSliceIconProps) => (
  <IndexedIcon
    {...legendProps}
    iconByIndex={pathSliceIcons}
    active={active}
    color={theme.colors.mainBlue}
  />
);
